import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { useAttribution } from "@/hooks/useAttribution";
import { X, MapPin, Star, ChevronDown, ChevronUp, Users, Loader2, Sparkles, AlertCircle, AlertTriangle, Navigation, Check, RotateCcw, Utensils, Car, Home, Plus, Heart, Meh, ThumbsDown, Search, Camera, Edit2, ChevronLeft, ChevronRight, Trash2, Play, UserPlus, Share2, Images, Building2, Trees, MoreHorizontal } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import CameraFirstCapture, { type CapturedMedia } from "./CameraFirstCapture";
import QuickTagSelector, { type MediaContentType } from "./QuickTagSelector";
import { RestaurantSearchInput, type SelectedRestaurant } from "./RestaurantSearchInput";
import { extractExifData, type GPSCoordinates } from "@/lib/exifUtils";
import { queryClient } from "@/lib/queryClient";
import { Link, useLocation } from "wouter";
import { useUserProfileOptional } from "@/hooks/useUserProfile";
import { useAuth } from "@/hooks/useAuth";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import GeolocationPrompt, { useLocationPreference, requestGeolocation } from "./GeolocationPrompt";
import { resizeForClassification, resizeForDisplay } from "@/lib/imageUtils";
import { useTags } from "@/hooks/useTags";
import { useKudozBreakdown } from "@/hooks/useKudozBreakdown";
import { KudozModule } from "./KudozModule";
import TagFriendsPopover, { SelectedFriendChips } from "./TagFriendsPopover";

// Extended media item with per-item metadata for multi-photo support
interface MediaItemWithMeta {
  id: string;
  file: File;
  url: string;
  type: 'photo' | 'video';
  gpsCoordinates?: GPSCoordinates;
  classification?: ClassificationResult;
  itemName?: string;
  itemDescription?: string;
  itemPrice?: string;
  itemRating?: 1 | 2 | 3;
  menuItemId?: string;
  croppedBlob?: Blob;
  croppedUrl?: string;
  isClassifying?: boolean;
}

export type ServiceType = 'dine-in' | 'takeout' | 'delivery';

interface RatingReference {
  reviewId: string;
  restaurant: string;
  restaurantId: string;
  score: number;
  createdAt: string;
}

interface CameraFirstReviewProps {
  isOpen: boolean;
  onClose: () => void;
  onReviewSubmitted?: (reviewData: any) => void;
  initialRestaurant?: SelectedRestaurant;
  detectedGPS?: GPSCoordinates;
}

interface ClassificationResult {
  contentType: MediaContentType;
  confidence: number;
  suggestedTags: string[];
  detectedItems?: string[];
  cuisineHint?: string;
  available: boolean;
}

interface RestaurantMatch {
  source: 'local' | 'google';
  id?: string;
  placeId?: string;
  name: string;
  address: string;
  city: string;
  state: string;
  category: string | null;
}

type FlowStep = 'restaurant' | 'camera' | 'processing' | 'review';

// Type for following list users (for Tag Friends feature)
interface FollowingUser {
  id: string;
  displayName: string | null;
  username: string | null;
  profilePhotoUrl: string | null;
}


export default function CameraFirstReview({
  isOpen,
  onClose,
  onReviewSubmitted,
  initialRestaurant,
  detectedGPS,
}: CameraFirstReviewProps) {
  const { toast } = useToast();
  const { sendAttributionEvent } = useAttribution();
  const userProfile = useUserProfileOptional();
  const { isAuthenticated, isGuestAccount } = useAuth();
  const [, navigateTo] = useLocation();
  
  const [step, setStep] = useState<FlowStep>('restaurant');
  // Multi-media support: array of media items with per-item metadata
  const [mediaItems, setMediaItems] = useState<MediaItemWithMeta[]>([]);
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const [isClassifying, setIsClassifying] = useState(false);
  const [isLookingUpRestaurant, setIsLookingUpRestaurant] = useState(false);
  const [restaurantSuggestions, setRestaurantSuggestions] = useState<RestaurantMatch[]>([]);
  const [showLocationPrompt, setShowLocationPrompt] = useState(false);
  const [locationPromptMode, setLocationPromptMode] = useState<'initial' | 'denied'>('initial');
  
  const { preference: locationPreference, savePreference: saveLocationPreference } = useLocationPreference();

  // Fetch user's prior review ratings for comparison notches on slider
  const { data: priorReviews = [] } = useQuery<RatingReference[]>({
    queryKey: ['/api/reviews/rating-references'],
    enabled: isOpen,
  });
  
  const userId = userProfile?.profile?.userId;

  // Fetch user profile stats to detect first-ever review (for Kudoz breakdown)
  const { data: userStats } = useQuery<{ totalReviews: number }>({
    queryKey: ['/api/user/profile'],
    enabled: isOpen && !!userId,
    select: (data: any) => ({ totalReviews: data?.totalReviews ?? 0 }),
  });
  const isFirstReview = (userStats?.totalReviews ?? 0) === 0;

  // Fetch user's following list for Tag Friends feature
  const { data: followingData } = useQuery<{ users: FollowingUser[], total: number, canView: boolean }>({
    queryKey: ['/api/users', userId, 'following'],
    enabled: isOpen && !!userId,
  });
  
  const [selectedRestaurant, setSelectedRestaurant] = useState<SelectedRestaurant | null>(initialRestaurant || null);
  const [restaurantSearchQuery, setRestaurantSearchQuery] = useState("");
  const [rating, setRating] = useState([5]); // 1-10 restaurant/experience rating
  const [itemRating, setItemRating] = useState<1 | 2 | 3>(3); // 1-3 item rating: 1=Hated, 2=OK, 3=Loved
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [reviewText, setReviewText] = useState("");
  const [showPostWithoutTextDialog, setShowPostWithoutTextDialog] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [visitDate, setVisitDate] = useState(new Date().toISOString().split('T')[0]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitPhase, setSubmitPhase] = useState<'idle' | 'uploading' | 'posting'>('idle');
  
  const [taggedFriends, setTaggedFriends] = useState<string[]>([]);
  
  // Service type (dine-in, takeout, delivery)
  const [serviceType, setServiceType] = useState<ServiceType>('dine-in');
  const [gpsMatchedRestaurant, setGpsMatchedRestaurant] = useState(false);

  // Restaurant step: pre-selected files (gallery path) and pre-acquired stream (camera path)
  const [initialFiles, setInitialFiles] = useState<CapturedMedia[]>([]);
  const [initialStream, setInitialStream] = useState<MediaStream | null>(null);
  // Hidden file input ref for the restaurant step gallery button
  const restaurantGalleryInputRef = useRef<HTMLInputElement>(null);
  // Camera support detection — async enumerateDevices check for actual camera hardware
  const [hasCameraSupport, setHasCameraSupport] = useState(
    typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia
  );
  useEffect(() => {
    if (!navigator.mediaDevices?.enumerateDevices) {
      setHasCameraSupport(false);
      return;
    }
    navigator.mediaDevices.enumerateDevices()
      .then(devices => setHasCameraSupport(devices.some(d => d.kind === 'videoinput')))
      .catch(() => setHasCameraSupport(false));
  }, []);

  // Constant for video size limit (mirrors CameraFirstCapture)
  const MAX_VIDEO_SIZE_BYTES = 100 * 1024 * 1024;

  // Menu item suggestions for Tier 2 restaurants (per-item editing)
  const [menuItemSearchQuery, setMenuItemSearchQuery] = useState("");
  const [showMenuItemSearch, setShowMenuItemSearch] = useState(false);
  // Tracks which restaurant ID has already triggered the menu-match toast (prevents re-firing)
  const menuToastShownForRef = useRef<string | null>(null);
  
  // Editing state for current media item
  const [isEditingItemName, setIsEditingItemName] = useState(false);
  const [editingItemNameValue, setEditingItemNameValue] = useState("");
  
  // Swipe gesture state for media carousel
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const minSwipeDistance = 50;
  
  // Haptic feedback helper
  const triggerHaptic = useCallback((pattern: number | number[] = 10) => {
    if ('vibrate' in navigator) {
      navigator.vibrate(pattern);
    }
  }, []);
  
  
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  }, []);
  
  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  }, []);
  
  const handleTouchEnd = useCallback(() => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe && mediaItems.length > 1) {
      setCurrentMediaIndex(prev => prev < mediaItems.length - 1 ? prev + 1 : 0);
    } else if (isRightSwipe && mediaItems.length > 1) {
      setCurrentMediaIndex(prev => prev > 0 ? prev - 1 : mediaItems.length - 1);
    }
  }, [touchStart, touchEnd, mediaItems.length]);

  // Check if selected restaurant is Tier 2 (has Kudoz backend integration)
  const isTier2Restaurant = Boolean(selectedRestaurant?.kudozExternalId);
  
  // Get current media item being viewed/edited
  const currentMedia = mediaItems[currentMediaIndex];
  
  // Kudoz rewards breakdown
  const kudozBreakdown = useKudozBreakdown({
    hasVibeRating: rating[0] > 0,
    mediaUploads: mediaItems.map(m => ({ type: m.type })),
    dishRatings: mediaItems
      .filter(m => m.itemName)
      .map(m => ({ rating: m.itemRating === 3 ? 'loved' : m.itemRating === 2 ? 'fine' : m.itemRating === 1 ? 'disliked' : null })),
    sharingSettings: { mode: 'public', taggedFriends: [] }, // Camera-first reviews default to public
    isFirstReview,
  });
  
  // Get primary classification (from first food-item or first media)
  const primaryClassification = useMemo(() => {
    const foodItem = mediaItems.find(m => m.classification?.contentType === 'food-item');
    return foodItem?.classification || mediaItems[0]?.classification || null;
  }, [mediaItems]);

  // Auto-save draft to localStorage (text-based fields only, not media)
  const DRAFT_KEY = 'camera-first-review-draft';
  const [draftRestored, setDraftRestored] = useState(false);
  
  // Restore draft on open (if available and less than 24 hours old)
  useEffect(() => {
    if (isOpen && !draftRestored) {
      try {
        const savedDraft = localStorage.getItem(DRAFT_KEY);
        if (savedDraft) {
          const draft = JSON.parse(savedDraft);
          const ageHours = (Date.now() - draft.savedAt) / (1000 * 60 * 60);
          if (ageHours < 24) {
            // Restore text-based fields (rating, text, tags, service type, visit date)
            if (draft.rating) setRating([draft.rating]);
            if (draft.reviewText) setReviewText(draft.reviewText);
            if (draft.selectedTags?.length) setSelectedTags(draft.selectedTags);
            if (draft.serviceType) setServiceType(draft.serviceType);
            if (draft.visitDate) setVisitDate(draft.visitDate);
            if (draft.taggedFriends?.length) setTaggedFriends(draft.taggedFriends);
            console.log('[CameraFirstReview] Restored draft from', new Date(draft.savedAt).toLocaleString());
          } else {
            localStorage.removeItem(DRAFT_KEY); // Expired draft
          }
        }
      } catch (e) {
        console.warn('[CameraFirstReview] Failed to restore draft:', e);
      }
      setDraftRestored(true);
    }
  }, [isOpen, draftRestored]);
  
  // Save draft when form state changes
  useEffect(() => {
    if (step === 'review' && selectedRestaurant) {
      const draft = {
        restaurantName: selectedRestaurant.name,
        restaurantId: selectedRestaurant.id,
        rating: rating[0],
        reviewText,
        selectedTags,
        serviceType,
        visitDate,
        taggedFriends,
        savedAt: Date.now(),
      };
      localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
    }
  }, [step, selectedRestaurant, rating, reviewText, selectedTags, serviceType, visitDate, taggedFriends]);
  
  // Clear draft on successful submission
  const clearDraft = useCallback(() => {
    localStorage.removeItem(DRAFT_KEY);
  }, []);
  
  // Dismiss keyboard on tap outside inputs (mobile UX)
  const handleContentClick = useCallback((e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    // Only blur if clicking on non-interactive elements (not buttons, selects, inputs, etc.)
    const isInteractiveElement = target.tagName === 'INPUT' || 
                                  target.tagName === 'TEXTAREA' || 
                                  target.tagName === 'BUTTON' ||
                                  target.tagName === 'SELECT' ||
                                  target.closest('input, textarea, button, select, [role="button"], [contenteditable="true"]');
    if (!isInteractiveElement && document.activeElement instanceof HTMLElement && 
        (document.activeElement.tagName === 'INPUT' || document.activeElement.tagName === 'TEXTAREA')) {
      document.activeElement.blur();
    }
  }, []);
  
  // Get tags for auto-selection — filtered to only tags valid for the current service type
  const { tagCategories } = useTags();
  const allTagLabels = useMemo(() => {
    if (!tagCategories) return [];
    return tagCategories
      .filter(cat => cat.experienceTypes.includes(serviceType))
      .flatMap(cat => cat.tags.filter(t => t.orderTypes.includes(serviceType)).map(t => t.label));
  }, [tagCategories, serviceType]);
  
  // Auto-select AI-suggested tags when classification completes (use all media items' tags)
  useEffect(() => {
    if (mediaItems.length > 0 && allTagLabels.length > 0 && selectedTags.length === 0) {
      const allSuggestedTags = mediaItems.flatMap(m => m.classification?.suggestedTags || []);
      const uniqueSuggested = Array.from(new Set(allSuggestedTags));
      if (uniqueSuggested.length > 0) {
        const matchedTags = allTagLabels.filter(tag => 
          uniqueSuggested.some(suggested => 
            tag.toLowerCase().includes(suggested.toLowerCase()) ||
            suggested.toLowerCase().includes(tag.toLowerCase())
          )
        );
        if (matchedTags.length > 0) {
          console.log('[CameraFirstReview] Auto-selecting AI suggested tags:', matchedTags);
          setSelectedTags(matchedTags.slice(0, 5));
        }
      }
    }
  }, [mediaItems, allTagLabels, selectedTags.length]);

  // Auto-select cuisine tag when a restaurant is chosen
  useEffect(() => {
    if (!selectedRestaurant || !tagCategories) return;
    const cuisineCat = tagCategories.find(cat => cat.id === 'cuisine');
    if (!cuisineCat) return;
    const cuisineHint = (selectedRestaurant.brandCategoryName || selectedRestaurant.category || '').toLowerCase().trim();
    if (!cuisineHint) return;
    const matchingTag = cuisineCat.tags.find(tag =>
      tag.label.toLowerCase().includes(cuisineHint) || cuisineHint.includes(tag.label.toLowerCase())
    );
    if (matchingTag) {
      setSelectedTags(prev =>
        prev.includes(matchingTag.label) ? prev : [...prev, matchingTag.label]
      );
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRestaurant?.id, tagCategories]);

  // Fetch menu items for Tier 2 restaurants when there is any media to review
  const hasFoodItems = isTier2Restaurant && mediaItems.length > 0;
  const { data: menuData, isLoading: isLoadingMenu } = useQuery<{ 
    categories: { name: string; items: { id: string; name: string; description?: string; imageUrl?: string }[] }[] 
  }>({
    queryKey: ['/api/restaurants', selectedRestaurant?.id, 'menu'],
    enabled: isOpen && isTier2Restaurant && hasFoodItems && !!selectedRestaurant?.id,
  });
  
  // Flatten all menu items for Tier 2 restaurants
  const allMenuItems = useMemo(() => {
    if (!menuData?.categories) return [];
    const items: { id: string; name: string; category: string }[] = [];
    menuData.categories.forEach(cat => {
      cat.items.forEach(item => {
        items.push({ id: item.id, name: item.name, category: cat.name });
      });
    });
    return items;
  }, [menuData]);

  // Find menu items that match AI detection for current media (for Tier 2)
  const matchedMenuItems = useMemo(() => {
    if (!allMenuItems.length || !currentMedia?.classification?.detectedItems?.length) return [];
    const detected = currentMedia.classification.detectedItems.map(d => d.toLowerCase());
    return allMenuItems.filter(item => 
      detected.some(d => item.name.toLowerCase().includes(d) || d.includes(item.name.toLowerCase()))
    ).slice(0, 8);
  }, [allMenuItems, currentMedia?.classification?.detectedItems]);

  // T002: Toast once per restaurant when menu data loads, directing user to the matched items
  useEffect(() => {
    if (!allMenuItems.length || !isTier2Restaurant || !hasFoodItems || !selectedRestaurant?.id) return;
    if (menuToastShownForRef.current === selectedRestaurant.id) return;

    menuToastShownForRef.current = selectedRestaurant.id;

    if (matchedMenuItems.length > 0) {
      toast({
        title: `${matchedMenuItems.length} menu match${matchedMenuItems.length !== 1 ? 'es' : ''} found`,
        description: `"${matchedMenuItems[0].name}" is on the ${selectedRestaurant.name} menu — scroll up to link it`,
      });
    } else {
      toast({
        title: 'Menu loaded',
        description: `Search for your dish in the ${selectedRestaurant.name} menu`,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allMenuItems, matchedMenuItems, isTier2Restaurant, hasFoodItems, selectedRestaurant?.id]);

  // Menu items to show as suggestions (prioritize matched items, fallback to all)
  // Deduplicate: exclude items whose name matches the AI-detected item name (case-insensitive)
  const menuItemSuggestions = useMemo(() => {
    const autoDetectedName = currentMedia?.itemName?.toLowerCase() || currentMedia?.classification?.detectedItems?.[0]?.toLowerCase();
    const base = matchedMenuItems.length > 0 ? matchedMenuItems : allMenuItems.slice(0, 8);
    if (!autoDetectedName) return base;
    const seen = new Set<string>();
    return base.filter(item => {
      const key = item.name.toLowerCase();
      if (seen.has(key) || key === autoDetectedName) return false;
      seen.add(key);
      return true;
    });
  }, [matchedMenuItems, allMenuItems, currentMedia?.itemName, currentMedia?.classification?.detectedItems]);

  // Filter all menu items based on search query
  const filteredMenuItems = useMemo(() => {
    if (!menuItemSearchQuery.trim()) return allMenuItems.slice(0, 15);
    const query = menuItemSearchQuery.toLowerCase().trim();
    return allMenuItems.filter(item => 
      item.name.toLowerCase().includes(query) || 
      item.category.toLowerCase().includes(query)
    ).slice(0, 15);
  }, [allMenuItems, menuItemSearchQuery]);

  useEffect(() => {
    if (!isOpen) {
      setStep('restaurant');
      // Revoke all media URLs to prevent memory leaks
      mediaItems.forEach(item => {
        if (item.url) URL.revokeObjectURL(item.url);
        if (item.croppedUrl) URL.revokeObjectURL(item.croppedUrl);
      });
      // Only reset arrays if they're not already empty (prevents infinite loop from new array references)
      if (mediaItems.length > 0) setMediaItems([]);
      setCurrentMediaIndex(0);
      if (restaurantSuggestions.length > 0) setRestaurantSuggestions([]);
      setSelectedRestaurant(initialRestaurant || null);
      setRestaurantSearchQuery("");
      setRating([5]);
      if (selectedTags.length > 0) setSelectedTags([]);
      setReviewText("");
      setShowPostWithoutTextDialog(false);
      if (taggedFriends.length > 0) setTaggedFriends([]);
      setServiceType('dine-in');
      setGpsMatchedRestaurant(false);
      setMenuItemSearchQuery("");
      setShowMenuItemSearch(false);
      setIsEditingItemName(false);
      setEditingItemNameValue("");
      menuToastShownForRef.current = null;
      // Clear restaurant-step pre-loaded media
      setInitialFiles([]);
      if (initialStream) {
        initialStream.getTracks().forEach(t => t.stop());
        setInitialStream(null);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialRestaurant]); // Removed mediaItems dependency to prevent infinite loop

  // T001: Start GPS lookup immediately on modal open if permission already granted
  useEffect(() => {
    if (!isOpen || selectedRestaurant || locationPreference !== 'allowed') return;
    navigator.geolocation.getCurrentPosition(
      (pos) => lookupRestaurantByGPS({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
      () => { /* silent fail — existing GeolocationPrompt handles denied/unavailable cases */ },
      { timeout: 5000, maximumAge: 60_000 }
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]); // Only re-run when modal opens/closes — guards inside prevent spurious calls

  // (Intentionally not auto-focusing or scrolling on review step — let the user start from the top/media area)

  // Classify media and return the result (doesn't set global state)
  const classifyMediaItem = useCallback(async (media: CapturedMedia | { file: File; url: string; type: 'photo' | 'video' }): Promise<ClassificationResult> => {
    const defaultResult: ClassificationResult = {
      contentType: 'general',
      confidence: 0,
      suggestedTags: [],
      available: false,
    };
    
    try {
      const rawToken = localStorage.getItem('auth_token') || localStorage.getItem('token');
      const token = rawToken && rawToken !== 'null' && rawToken !== 'undefined' ? rawToken : null;
      
      if (media.type === 'video') {
        const video = document.createElement('video');
        video.src = media.url;
        video.currentTime = 1;
        
        await new Promise((resolve) => {
          video.onloadeddata = resolve;
        });
        
        const canvas = document.createElement('canvas');
        const maxDim = 512;
        const scale = Math.min(maxDim / video.videoWidth, maxDim / video.videoHeight);
        canvas.width = Math.round(video.videoWidth * scale);
        canvas.height = Math.round(video.videoHeight * scale);
        const ctx = canvas.getContext('2d');
        ctx?.drawImage(video, 0, 0, canvas.width, canvas.height);
        
        const frameBase64 = canvas.toDataURL('image/jpeg', 0.7).split(',')[1];
        
        const response = await fetch('/api/media/classify', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ imageBase64: frameBase64, type: 'video' }),
        });
        
        if (response.ok) {
          return await response.json() as ClassificationResult;
        }
        return defaultResult;
      } else {
        const { base64, compressionInfo } = await resizeForClassification(media.file);
        console.log('[CameraFirstReview] Image optimized for classification:', compressionInfo);
        
        const response = await fetch('/api/media/classify', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ imageBase64: base64, type: 'image' }),
        });
        
        if (response.ok) {
          return await response.json() as ClassificationResult;
        }
        return defaultResult;
      }
    } catch (error) {
      console.error('[CameraFirstReview] Classification error:', error);
      return defaultResult;
    }
  }, []);

  const lookupRestaurantByGPS = useCallback(async (gps: GPSCoordinates) => {
    setIsLookingUpRestaurant(true);
    try {
      const response = await fetch(
        `/api/restaurants/by-gps?latitude=${gps.latitude}&longitude=${gps.longitude}&radius=50`
      );
      
      if (response.ok) {
        const data = await response.json();
        const suggestions: RestaurantMatch[] = [
          ...data.local,
          ...data.google,
        ];
        setRestaurantSuggestions(suggestions);
        
        if (data.bestMatch && !selectedRestaurant) {
          const match = data.bestMatch;
          setSelectedRestaurant({
            id: match.id || '',
            name: match.name,
            address: match.address,
            city: match.city,
            state: match.state,
            googlePlaceId: match.placeId || match.googlePlaceId || null,
            isNew: match.source === 'google',
            category: match.category,
            brandCategoryName: match.brandCategoryName,
            kudozExternalId: match.kudozExternalId,
            logoImageUrl: match.logoImageUrl || null,
            primaryImageUrl: match.primaryImageUrl || null,
            googlePhotoName: match.googlePhotoName || null,
          });
          setRestaurantSearchQuery(match.name);
          // GPS matched = likely dine-in
          setGpsMatchedRestaurant(true);
          setServiceType('dine-in');
        }
      }
    } catch (error) {
      console.error('[CameraFirstReview] GPS lookup error:', error);
    } finally {
      setIsLookingUpRestaurant(false);
    }
  }, [selectedRestaurant]);

  const handleSkip = useCallback(() => setStep('review'), []);

  // Restaurant step: gallery button — file picker must be triggered directly in the click handler
  const handleRestaurantGalleryClick = useCallback(() => {
    restaurantGalleryInputRef.current?.click();
  }, []);

  const handleRestaurantGalleryChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;

    const newMedia: CapturedMedia[] = [];
    let hasOversizeVideo = false;

    for (const file of files) {
      const isVideo = file.type.startsWith('video/');
      if (isVideo && file.size > MAX_VIDEO_SIZE_BYTES) {
        hasOversizeVideo = true;
        continue;
      }
      newMedia.push({
        type: isVideo ? 'video' : 'photo',
        file,
        url: URL.createObjectURL(file),
      });
    }

    if (hasOversizeVideo) {
      toast({
        title: 'Video too large',
        description: 'Some videos exceeded 100 MB and were skipped. Please choose shorter clips.',
        variant: 'destructive',
      });
    }

    if (!newMedia.length) return;

    // Stop any in-flight camera stream from a previous camera button tap
    setInitialStream(prev => { prev?.getTracks().forEach(t => t.stop()); return null; });
    setInitialFiles(newMedia);
    setStep('camera');
  }, [MAX_VIDEO_SIZE_BYTES, toast]);

  // Restaurant step: camera button — getUserMedia MUST be called synchronously in the click handler
  const handleRestaurantCameraClick = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } },
        audio: true,
      });
      setInitialFiles([]); // Clear any gallery files selected on a previous tap
      setInitialStream(stream);
      setStep('camera');
    } catch (err) {
      const name = err instanceof Error ? err.name : '';
      if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
        toast({ title: 'Camera access denied', description: 'Allow camera access in your browser settings, then try again.', variant: 'destructive' });
      } else if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
        toast({ title: 'No camera found', description: 'No camera was found on this device. Choose from your gallery instead.', variant: 'destructive' });
      } else {
        toast({ title: 'Camera unavailable', description: 'Unable to start camera. Try choosing from your gallery.', variant: 'destructive' });
      }
    }
  }, [toast]);

  const handleCapture = useCallback(async (media: CapturedMedia) => {
    // Clear pre-loaded files/stream so they don't re-apply if the user returns to camera step
    setInitialFiles([]);
    setInitialStream(null);
    setStep('processing');
    setIsClassifying(true);

    const isPhoto = media.type === 'photo';

    const newMediaItem: MediaItemWithMeta = {
      id: `media-${Date.now()}`,
      file: media.file,
      url: media.url,
      type: isPhoto ? 'photo' : 'video',
      gpsCoordinates: media.gpsCoordinates,
      ...(isPhoto && { croppedUrl: media.url }),
      itemRating: 3,
      isClassifying: true,
    };

    setMediaItems(prev => [...prev, newMediaItem]);
    setCurrentMediaIndex(prev => prev + (mediaItems.length === 0 ? 0 : 1));

    const classifySource = isPhoto
      ? { file: media.file, url: media.url, type: 'photo' as const }
      : media;
    const classification = await classifyMediaItem(classifySource);

    setMediaItems(prev => prev.map(item =>
      item.id === newMediaItem.id
        ? { ...item, classification, isClassifying: false, itemName: classification.detectedItems?.[0] || undefined }
        : item
    ));

    setIsClassifying(false);

    if (mediaItems.length === 0) {
      let hasGPS = false;
      if (media.gpsCoordinates) {
        lookupRestaurantByGPS(media.gpsCoordinates);
        hasGPS = true;
      } else {
        const exifData = await extractExifData(media.file);
        if (exifData.gps) {
          lookupRestaurantByGPS(exifData.gps);
          hasGPS = true;
        } else if (detectedGPS) {
          lookupRestaurantByGPS(detectedGPS);
          hasGPS = true;
        }
      }

      if (!hasGPS && locationPreference === 'not_asked') {
        setLocationPromptMode('initial');
        setShowLocationPrompt(true);
      }
    }

    setTimeout(() => { setStep('review'); }, 500);
  }, [classifyMediaItem, lookupRestaurantByGPS, detectedGPS, locationPreference, mediaItems.length]);
  
  const handleLocationAllow = useCallback(async () => {
    setShowLocationPrompt(false);
    saveLocationPreference('allowed');
    
    const position = await requestGeolocation();
    if (position) {
      lookupRestaurantByGPS({
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
      });
      toast({
        title: "Location detected",
        description: "Searching for nearby restaurants...",
      });
    } else {
      setLocationPromptMode('denied');
      setShowLocationPrompt(true);
      saveLocationPreference('denied');
    }
  }, [saveLocationPreference, lookupRestaurantByGPS, toast]);
  
  const handleLocationDeny = useCallback(() => {
    setShowLocationPrompt(false);
    saveLocationPreference('denied');
  }, [saveLocationPreference]);

  const handleSubmit = useCallback(async () => {
    if (isSubmitting) return;
    if (!selectedRestaurant) {
      toast({
        title: "Restaurant required",
        description: "Please select or search for a restaurant",
        variant: "destructive",
      });
      return;
    }

    // Pre-flight: reject oversized videos before attempting any upload
    const MAX_VIDEO_SIZE = 100 * 1024 * 1024; // 100 MB (Cloudflare Pro cap)
    const oversizedVideos = mediaItems.filter(
      item => item.type === 'video' && item.file.size > MAX_VIDEO_SIZE
    );
    if (oversizedVideos.length > 0) {
      const sizeMB = (oversizedVideos[0].file.size / (1024 * 1024)).toFixed(1);
      toast({
        title: "Video too large",
        description: `Your video is ${sizeMB} MB. Videos must be under 100 MB. Please remove it and record a shorter clip.`,
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    setSubmitPhase('uploading');

    try {
      // Step 1: Pre-upload all media files individually via presigned URLs
      const uploadedMedia: Array<{ url: string; type: 'photo' | 'video' }> = [];
      const mediaMetadata: Array<{
        contentType: string;
        itemName?: string;
        itemDescription?: string;
        itemPrice?: string;
        itemRating?: number;
        menuItemId?: string;
      }> = [];

      for (let index = 0; index < mediaItems.length; index++) {
        const item = mediaItems[index];
        const isVideo = item.type === 'video';

        let fileToUpload: File;
        if (isVideo) {
          fileToUpload = item.file;
        } else {
          try {
            const sourceBlob = item.croppedBlob || item.file;
            const sourceFile = sourceBlob instanceof File
              ? sourceBlob
              : new File([sourceBlob], `photo-${index}.jpg`, { type: 'image/jpeg' });
            const resized = await resizeForDisplay(sourceFile, 1200);
            fileToUpload = new File([resized.blob], `photo-${index}.jpg`, { type: 'image/jpeg' });
          } catch {
            const sourceBlob = item.croppedBlob || item.file;
            fileToUpload = sourceBlob instanceof File
              ? sourceBlob
              : new File([sourceBlob], `photo-${index}.jpg`, { type: 'image/jpeg' });
          }
        }

        const formData = new FormData();
        formData.append('file', fileToUpload);

        const authToken = localStorage.getItem('auth_token') || localStorage.getItem('token');
        const eatzyToken = localStorage.getItem('eatzyUserToken') ?? '';
        const uploadResponse = await fetch('/api/media/upload-cdn', {
          method: 'POST',
          credentials: 'include',
          headers: {
            ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {}),
            ...(eatzyToken ? { 'x-eatzy-token': eatzyToken } : {}),
          },
          body: formData,
        });

        if (!uploadResponse.ok) {
          const errorData = await uploadResponse.json().catch(() => ({}));
          throw new Error(errorData.error || `Failed to upload media item ${index + 1}. Please try again.`);
        }

        const uploadResult = await uploadResponse.json();
        if (!uploadResult?.url) {
          throw new Error(`Upload succeeded but no URL returned for media item ${index + 1}.`);
        }

        uploadedMedia.push({
          url: uploadResult.url,
          type: isVideo ? 'video' : 'photo',
        });

        mediaMetadata.push({
          contentType: item.classification?.contentType || 'general',
          itemName: item.itemName,
          itemDescription: item.itemDescription,
          itemPrice: item.itemPrice,
          itemRating: item.itemName ? item.itemRating : undefined,
          menuItemId: item.menuItemId,
        });
      }

      // Step 2: Submit review as JSON with pre-uploaded media URLs
      setSubmitPhase('posting');

      const reviewPayload: Record<string, unknown> = {
        restaurantId: selectedRestaurant.id || '',
        restaurantName: selectedRestaurant.name,
        rating: rating[0],
        text: reviewText,
        tags: selectedTags,
        visitDate,
        serviceType,
        ...(mediaItems.length > 0 && {
          mediaContentType: primaryClassification?.contentType || 'general',
          mediaType: mediaItems[0].type === 'photo' ? 'image' : mediaItems[0].type,
        }),
        ...(taggedFriends.length > 0 && { companions: taggedFriends }),
        ...(selectedRestaurant.googlePlaceId && selectedRestaurant.isNew && {
          googlePlaceId: selectedRestaurant.googlePlaceId,
          city: selectedRestaurant.city,
          state: selectedRestaurant.state,
          address: selectedRestaurant.address,
          category: selectedRestaurant.category || '',
          ...(selectedRestaurant.googlePhotoName && {
            googlePhotoName: selectedRestaurant.googlePhotoName,
          }),
        }),
        ...(uploadedMedia.length > 0 && {
          media: uploadedMedia,
          mediaMetadata,
        }),
      };

      const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
      const validToken = token && token !== 'null' && token !== 'undefined' ? token : null;
      const response = await fetch('/api/reviews', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(validToken ? { 'Authorization': `Bearer ${validToken}` } : {}),
        },
        body: JSON.stringify(reviewPayload),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create review');
      }

      const result = await response.json();

      sendAttributionEvent('review_written', 'review', result.id ?? result.review?.id ?? '');

      queryClient.invalidateQueries({ queryKey: ['/api/reviews'] });
      queryClient.invalidateQueries({ queryKey: ['/api/user/reviews'] });

      toast({
        title: "Review posted!",
        description: "Your review has been shared successfully",
      });

      clearDraft();
      onReviewSubmitted?.(result);
      onClose();
    } catch (error) {
      console.error('[CameraFirstReview] Submit error:', error);
      toast({
        title: "Failed to post review",
        description: error instanceof Error ? error.message : "Please try again",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
      setSubmitPhase('idle');
    }
  }, [selectedRestaurant, mediaItems, primaryClassification, rating, reviewText, selectedTags, visitDate, serviceType, taggedFriends, toast, onClose, onReviewSubmitted, clearDraft]);

  const getRatingLabel = (value: number): string => {
    if (value >= 9) return "Outstanding";
    if (value >= 8) return "Excellent";
    if (value >= 7) return "Very Good";
    if (value >= 6) return "Good";
    if (value >= 5) return "Average";
    if (value >= 4) return "Below Average";
    return "Poor";
  };

  const getRatingColor = (value: number): string => {
    if (value >= 8) return "text-green-500";
    if (value >= 6) return "text-yellow-500";
    return "text-red-500";
  };

  if (!isOpen) return null;

  // ─── GUEST / UNAUTHENTICATED GATE ───────────────────────────────────────────
  if (!isAuthenticated || isGuestAccount) {
    return (
      <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <SheetContent side="bottom" className="rounded-t-2xl">
          <SheetHeader className="text-left">
            <SheetTitle>Full account required</SheetTitle>
          </SheetHeader>
          <div className="py-4 space-y-4">
            <p className="text-sm text-muted-foreground">
              You need a full Eatzy account to post reviews and earn Kudoz rewards.
            </p>
            <div className="flex gap-3">
              <Button
                className="flex-1 bg-[#0D9C97] text-white"
                onClick={() => {
                  onClose();
                  navigateTo('/signup');
                }}
              >
                Sign Up
              </Button>
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  onClose();
                  navigateTo('/login');
                }}
              >
                Log In
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  // ─── RESTAURANT STEP ────────────────────────────────────────────────────────
  if (step === 'restaurant') {
    return (
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="max-w-lg w-full flex flex-col h-[90svh] max-h-[90svh] p-0 gap-0">
          {/* Header */}
          <div className="flex-shrink-0 bg-background border-b px-4 py-3 flex items-center justify-between gap-2">
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="w-5 h-5" />
            </Button>
            <h2 className="font-semibold text-sm">Share Your Experience</h2>
            <div className="w-9" />
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto p-5 flex flex-col gap-5">
            <div>
              <h3 className="text-xl font-semibold mb-1">Which restaurant?</h3>
              <p className="text-sm text-muted-foreground">Search by name or location</p>
            </div>

            {/* Restaurant search */}
            {isLookingUpRestaurant && !selectedRestaurant ? (
              <div className="flex items-center gap-2 p-3 border rounded-md">
                <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Finding nearby restaurant…</span>
              </div>
            ) : (
              <RestaurantSearchInput
                value={restaurantSearchQuery}
                onChange={(name) => {
                  setRestaurantSearchQuery(name);
                  if (!name && selectedRestaurant) setSelectedRestaurant(null);
                }}
                onRestaurantSelect={(restaurant) => {
                  setSelectedRestaurant(restaurant);
                  setRestaurantSearchQuery(restaurant?.name || "");
                }}
                selectedRestaurant={selectedRestaurant}
                userLatitude={userProfile?.primaryAddress?.latitude}
                userLongitude={userProfile?.primaryAddress?.longitude}
              />
            )}

            {/* Nearby GPS suggestions (when not yet selected) */}
            {restaurantSuggestions.length > 1 && !selectedRestaurant && (
              <div className="flex flex-wrap gap-2">
                <span className="text-xs text-muted-foreground self-center">Nearby:</span>
                {restaurantSuggestions.slice(0, 3).map((r, i) => (
                  <Badge
                    key={i}
                    variant="outline"
                    className="cursor-pointer"
                    onClick={() => {
                      const matched: SelectedRestaurant = {
                        id: r.id || '',
                        name: r.name,
                        address: r.address,
                        city: r.city,
                        state: r.state,
                        googlePlaceId: r.placeId || null,
                        isNew: r.source === 'google',
                        category: r.category,
                      };
                      setSelectedRestaurant(matched);
                      setRestaurantSearchQuery(r.name);
                    }}
                  >
                    <MapPin className="w-3 h-3 mr-1" />
                    {r.name}
                  </Badge>
                ))}
              </div>
            )}

            {/* Revealed once restaurant is selected */}
            {selectedRestaurant && (
              <div className="flex flex-col gap-5">
                {/* GPS hint */}
                {gpsMatchedRestaurant && (
                  <p className="text-xs text-muted-foreground flex items-center gap-1 -mt-2">
                    <Navigation className="w-3 h-3" />
                    Auto-detected from your location
                  </p>
                )}

                {/* Cuisine tag row — pre-select tags matching the restaurant's cuisine */}
                {(() => {
                  const cuisineCat = tagCategories?.find(cat => cat.id === 'cuisine');
                  if (!cuisineCat) return null;
                  const cuisineHint = (selectedRestaurant.brandCategoryName || selectedRestaurant.category || '').toLowerCase();
                  // Sort: matching cuisine first, then alphabetical
                  const sorted = [...cuisineCat.tags].sort((a, b) => {
                    const aMatch = cuisineHint && (a.label.toLowerCase().includes(cuisineHint) || cuisineHint.includes(a.label.toLowerCase()));
                    const bMatch = cuisineHint && (b.label.toLowerCase().includes(cuisineHint) || cuisineHint.includes(b.label.toLowerCase()));
                    if (aMatch && !bMatch) return -1;
                    if (!aMatch && bMatch) return 1;
                    return a.label.localeCompare(b.label);
                  }).slice(0, 8);
                  return (
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Cuisine</p>
                      <div className="flex flex-wrap gap-2">
                        {sorted.map(tag => (
                          <Badge
                            key={tag.label}
                            variant={selectedTags.includes(tag.label) ? 'default' : 'outline'}
                            className="cursor-pointer"
                            onClick={() => {
                              setSelectedTags(prev =>
                                prev.includes(tag.label)
                                  ? prev.filter(t => t !== tag.label)
                                  : [...prev, tag.label]
                              );
                            }}
                          >
                            {tag.label}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {/* Service type selector */}
                <div className="space-y-2">
                  <label className="text-sm font-medium flex items-center gap-2">
                    <Utensils className="w-4 h-4" />
                    How did you visit?
                  </label>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant={serviceType === 'dine-in' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setServiceType('dine-in')}
                      className="flex-1"
                    >
                      <Utensils className="w-4 h-4 mr-1" />
                      Dine In
                    </Button>
                    <Button
                      type="button"
                      variant={serviceType === 'takeout' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setServiceType('takeout')}
                      className="flex-1"
                    >
                      <Car className="w-4 h-4 mr-1" />
                      Takeout
                    </Button>
                    <Button
                      type="button"
                      variant={serviceType === 'delivery' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setServiceType('delivery')}
                      className="flex-1"
                    >
                      <Home className="w-4 h-4 mr-1" />
                      Delivery
                    </Button>
                  </div>
                </div>

                {/* Media choice buttons */}
                <div className="flex flex-col gap-3">
                  <p className="text-sm font-medium">Add photos or videos</p>
                  <Button
                    type="button"
                    variant="outline"
                    size="default"
                    className="w-full h-14 text-base gap-3 justify-start px-5"
                    onClick={handleRestaurantGalleryClick}
                  >
                    <Images className="w-5 h-5 text-muted-foreground" />
                    <span>Choose from Gallery</span>
                  </Button>
                  {hasCameraSupport && (
                    <Button
                      type="button"
                      variant="outline"
                      size="default"
                      className="w-full h-14 text-base gap-3 justify-start px-5"
                      onClick={handleRestaurantCameraClick}
                    >
                      <Camera className="w-5 h-5 text-muted-foreground" />
                      <span>Take a Photo or Video</span>
                    </Button>
                  )}
                </div>

                {/* Skip link */}
                <button
                  type="button"
                  onClick={handleSkip}
                  className="text-sm text-muted-foreground underline underline-offset-2 text-center"
                >
                  Skip — write a text-only review
                </button>
              </div>
            )}
          </div>

          {/* Pre-rendered hidden file input — must exist BEFORE the button tap */}
          <input
            ref={restaurantGalleryInputRef}
            type="file"
            multiple
            accept="image/*,video/*"
            onChange={handleRestaurantGalleryChange}
            className="sr-only"
          />
        </DialogContent>
      </Dialog>
    );
  }

  if (step === 'camera') {
    return (
      <>
        <CameraFirstCapture
          isOpen={true}
          onClose={mediaItems.length > 0 ? () => setStep('review') : onClose}
          onCapture={handleCapture}
          onSkip={handleSkip}
          initialFiles={initialFiles.length > 0 ? initialFiles : undefined}
          initialStream={initialStream ?? undefined}
        />
        {/* Floating thumbnail strip overlay when adding more items */}
        {mediaItems.length > 0 && (
          <div className="fixed top-0 left-0 right-0 z-[60] bg-gradient-to-b from-black/80 to-transparent p-3 pt-safe">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-medium text-white">
                {mediaItems.length} {mediaItems.length === 1 ? 'dish' : 'dishes'} added
              </span>
              <Button
                variant="outline"
                size="sm"
                className="ml-auto text-xs bg-white/20 border-white/30 text-white"
                onClick={() => setStep('review')}
              >
                <ChevronLeft className="w-3 h-3 mr-1" />
                Back to review
              </Button>
            </div>
            <div className="flex gap-2 overflow-x-auto pb-1">
              {mediaItems.map((item, idx) => (
                <div 
                  key={idx}
                  className="flex-shrink-0 cursor-pointer"
                  onClick={() => {
                    setCurrentMediaIndex(idx);
                    setStep('review');
                  }}
                >
                  <div className="relative w-14 h-14 rounded-md overflow-hidden border-2 border-white/40">
                    {item.type === 'video' ? (
                      <video src={item.url} className="w-full h-full object-cover" muted playsInline preload="metadata" />
                    ) : (
                      <img 
                        src={item.croppedUrl || item.url} 
                        alt={`Item ${idx + 1}`}
                        className="w-full h-full object-cover"
                      />
                    )}
                    <div className="absolute top-0 left-0 bg-primary text-primary-foreground text-xs font-bold px-1.5 py-0.5 rounded-br">
                      {idx + 1}
                    </div>
                    {item.type === 'video' && (
                      <div className="absolute bottom-0.5 right-0.5 bg-black/60 rounded-full p-0.5">
                        <Play className="w-2.5 h-2.5 text-white fill-white" />
                      </div>
                    )}
                    {item.type === 'video' && item.file.size > 10 * 1024 * 1024 && (
                      <div className="absolute inset-0 flex items-center justify-center bg-destructive/70">
                        <AlertTriangle className="w-5 h-5 text-white" />
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-center text-white/80 mt-1 max-w-14 truncate">
                    {item.itemName || `Item ${idx + 1}`}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </>
    );
  }


  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg w-full flex flex-col h-[90svh] max-h-[90svh] p-0 gap-0">
        <div className="flex-shrink-0 bg-background border-b px-4 py-3 flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="w-5 h-5" />
            </Button>
            <h2 className="font-semibold">Share Your Experience</h2>
            <div className="w-9" /> {/* Spacer for symmetry */}
          </div>
          
          {/* Progress Breadcrumbs */}
          <div className="flex items-center justify-center gap-2 text-xs">
            <div className="flex items-center gap-1 px-2 py-0.5 rounded-full text-muted-foreground">
              <Check className="w-3 h-3 text-green-500" />
              <span>Capture</span>
            </div>
            <ChevronRight className="w-3 h-3 text-muted-foreground/50" />
            <div className={cn(
              "flex items-center gap-1 px-2 py-0.5 rounded-full transition-colors",
              (step === 'processing' || step === 'review') && !isSubmitting 
                ? "bg-primary/10 text-primary font-medium" 
                : "text-muted-foreground"
            )}>
              {step === 'processing' ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <Edit2 className="w-3 h-3" />
              )}
              <span>{step === 'processing' ? 'Processing' : 'Details'}</span>
            </div>
            <ChevronRight className="w-3 h-3 text-muted-foreground/50" />
            <div className={cn(
              "flex items-center gap-1 px-2 py-0.5 rounded-full transition-colors",
              isSubmitting ? "bg-primary/10 text-primary font-medium" : "text-muted-foreground"
            )}>
              {isSubmitting ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <Check className="w-3 h-3" />
              )}
              <span>Post</span>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-6" onClick={handleContentClick}>
          {step === 'processing' && (
            <div className="flex flex-col items-center justify-center py-12 gap-6 fm-fade-up">
              <div className="relative">
                <div className="absolute inset-0 rounded-full bg-primary/10 animate-ping opacity-60" />
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center relative">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
              </div>
              <div className="text-center space-y-1">
                <p className="font-medium">
                  {isClassifying ? "Analyzing your content" : isLookingUpRestaurant ? "Finding restaurants" : "Almost ready"}
                </p>
                <p className="text-sm text-muted-foreground">
                  {isClassifying ? "Using AI to detect what's in your photo..." : 
                   isLookingUpRestaurant ? "Looking up nearby restaurants from GPS..." : 
                   "Preparing your review..."}
                </p>
              </div>
            </div>
          )}

          {step === 'review' && (
            <>
              {/* Media Carousel with swipe support */}
              {mediaItems.length > 0 && (
              <div 
                className="relative rounded-lg overflow-hidden bg-muted aspect-video touch-pan-y"
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
              >
                {currentMedia?.type === 'video' ? (
                  <video
                    src={currentMedia.url}
                    className="w-full h-full object-cover"
                    controls
                    playsInline
                  />
                ) : currentMedia ? (
                  <img
                    src={currentMedia.croppedUrl || currentMedia.url}
                    alt="Captured"
                    className="w-full h-full object-cover pointer-events-none"
                  />
                ) : null}
                
                {/* Content type badge */}
                {currentMedia?.classification?.contentType && currentMedia.classification.contentType !== 'general' && (
                  <Badge 
                    className="absolute bottom-2 left-2 bg-black/60 text-white border-none"
                  >
                    <Sparkles className="w-3 h-3 mr-1" />
                    {currentMedia.classification.contentType.replace('-', ' ')}
                  </Badge>
                )}
                
                {/* Carousel navigation */}
                {mediaItems.length > 1 && (
                  <>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="absolute left-1 top-1/2 -translate-y-1/2 bg-black/40 text-white hover:bg-black/60 rounded-full h-8 w-8"
                      onClick={() => setCurrentMediaIndex(prev => prev > 0 ? prev - 1 : mediaItems.length - 1)}
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="absolute right-1 top-1/2 -translate-y-1/2 bg-black/40 text-white hover:bg-black/60 rounded-full h-8 w-8"
                      onClick={() => setCurrentMediaIndex(prev => prev < mediaItems.length - 1 ? prev + 1 : 0)}
                    >
                      <ChevronRight className="w-5 h-5" />
                    </Button>
                    
                    {/* Pagination dots */}
                    <div className="absolute bottom-2 right-2 flex gap-1">
                      {mediaItems.map((_, idx) => (
                        <button
                          key={idx}
                          onClick={() => setCurrentMediaIndex(idx)}
                          className={cn(
                            "w-2 h-2 rounded-full transition-colors",
                            idx === currentMediaIndex ? "bg-white" : "bg-white/50"
                          )}
                        />
                      ))}
                    </div>
                  </>
                )}
                
                {/* Delete current media button */}
                {mediaItems.length > 1 && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute top-1 right-1 bg-black/40 text-white hover:bg-red-500/80 rounded-full h-8 w-8"
                    onClick={() => {
                      const newItems = mediaItems.filter((_, idx) => idx !== currentMediaIndex);
                      setMediaItems(newItems);
                      if (currentMediaIndex >= newItems.length) {
                        setCurrentMediaIndex(Math.max(0, newItems.length - 1));
                      }
                    }}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
              )}

              {/* Photo category picker — shown for non-food-item photos so user can label the shot */}
              {currentMedia && currentMedia.type === 'photo' && currentMedia.classification?.contentType !== 'food-item' && (
                <div className="space-y-1.5">
                  <p className="text-xs text-muted-foreground">What type of photo is this?</p>
                  <div className="flex gap-2">
                    {([
                      { label: 'Inside', value: 'interior', Icon: Building2 },
                      { label: 'Outside', value: 'exterior', Icon: Trees },
                      { label: 'Other', value: 'general', Icon: MoreHorizontal },
                    ] as const).map(({ label, value, Icon }) => {
                      const isSelected = (currentMedia.classification?.contentType ?? 'general') === value;
                      return (
                        <Button
                          key={value}
                          type="button"
                          size="sm"
                          variant={isSelected ? 'default' : 'outline'}
                          className="flex-1 gap-1.5"
                          onClick={() => {
                            setMediaItems(prev => prev.map((m, idx) =>
                              idx === currentMediaIndex
                                ? {
                                    ...m,
                                    classification: m.classification
                                      ? { ...m.classification, contentType: value }
                                      : { contentType: value, confidence: 1, suggestedTags: [], available: true },
                                  }
                                : m
                            ));
                          }}
                        >
                          <Icon className="w-3.5 h-3.5" />
                          {label}
                        </Button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Restaurant compact affordance — already selected in restaurant step */}
              {selectedRestaurant ? (
                <div className="flex items-center gap-2 py-1.5 px-3 rounded-md bg-muted/50 text-sm">
                  <MapPin className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                  <span className="font-medium flex-1 truncate">{selectedRestaurant.name}</span>
                  <button
                    type="button"
                    onClick={() => setStep('restaurant')}
                    className="text-xs text-primary font-medium flex-shrink-0"
                    data-testid="button-change-restaurant-review"
                  >
                    Change
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  <label className="text-sm font-medium flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    Restaurant
                  </label>
                  <RestaurantSearchInput
                    value={restaurantSearchQuery}
                    onChange={(name) => {
                      setRestaurantSearchQuery(name);
                      if (!name && selectedRestaurant) setSelectedRestaurant(null);
                    }}
                    onRestaurantSelect={(restaurant) => {
                      setSelectedRestaurant(restaurant);
                      setRestaurantSearchQuery(restaurant?.name || "");
                    }}
                    selectedRestaurant={selectedRestaurant}
                    userLatitude={userProfile?.primaryAddress?.latitude}
                    userLongitude={userProfile?.primaryAddress?.longitude}
                  />
                </div>
              )}

              {/* Service Type Selector */}
              <div className="space-y-2">
                <label className="text-sm font-medium flex items-center gap-2">
                  <Utensils className="w-4 h-4" />
                  How did you visit?
                </label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant={serviceType === 'dine-in' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setServiceType('dine-in')}
                    className="flex-1"
                  >
                    <Utensils className="w-4 h-4 mr-1" />
                    Dine In
                  </Button>
                  <Button
                    type="button"
                    variant={serviceType === 'takeout' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setServiceType('takeout')}
                    className="flex-1"
                  >
                    <Car className="w-4 h-4 mr-1" />
                    Takeout
                  </Button>
                  <Button
                    type="button"
                    variant={serviceType === 'delivery' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setServiceType('delivery')}
                    className="flex-1"
                  >
                    <Home className="w-4 h-4 mr-1" />
                    Delivery
                  </Button>
                </div>
                {gpsMatchedRestaurant && serviceType === 'dine-in' && (
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    <Navigation className="w-3 h-3" />
                    Auto-detected from your location
                  </p>
                )}
              </div>

              {/* Menu Item & Rating for current photo - Tier 2 only */}
              {isTier2Restaurant && currentMedia && (
                <div className="space-y-3">
                  {/* Tier 2: Show menu item selector */}
                  {isTier2Restaurant && (
                    <>
                      <label className="text-sm font-medium flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-primary" />
                        Add a dish {mediaItems.length > 1 && `(Photo ${currentMediaIndex + 1})`}
                      </label>
                      {isLoadingMenu ? (
                        <div className="flex flex-wrap gap-2">
                          {[1, 2, 3].map((i) => (
                            <div key={i} className="h-7 w-20 bg-muted animate-pulse rounded-full" />
                          ))}
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {/* Search input for menu items */}
                          <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <input
                              type="text"
                              placeholder="Search menu items..."
                              value={menuItemSearchQuery}
                              onChange={(e) => {
                                setMenuItemSearchQuery(e.target.value);
                                setShowMenuItemSearch(true);
                              }}
                              onFocus={() => setShowMenuItemSearch(true)}
                              className="w-full pl-9 pr-3 py-2 text-sm border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary/20"
                            />
                            {menuItemSearchQuery && (
                              <button
                                type="button"
                                onClick={() => {
                                  setMenuItemSearchQuery("");
                                  setShowMenuItemSearch(false);
                                }}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                          
                          {/* Search results dropdown */}
                          {showMenuItemSearch && filteredMenuItems.length > 0 && (
                            <div className="border rounded-md bg-background shadow-md max-h-48 overflow-y-auto">
                              {filteredMenuItems.map((item) => (
                                <button
                                  key={item.id}
                                  type="button"
                                  onClick={() => {
                                    // Update current media item with selected menu item
                                    setMediaItems(prev => prev.map((m, idx) => 
                                      idx === currentMediaIndex 
                                        ? { ...m, menuItemId: item.id, itemName: item.name }
                                        : m
                                    ));
                                    setMenuItemSearchQuery("");
                                    setShowMenuItemSearch(false);
                                  }}
                                  className={cn(
                                    "w-full px-3 py-2 text-left text-sm flex items-center justify-between",
                                    "hover-elevate",
                                    currentMedia?.menuItemId === item.id && "bg-primary/10"
                                  )}
                                >
                                  <span className="truncate">{item.name}</span>
                                  <span className="text-xs text-muted-foreground ml-2 flex-shrink-0">{item.category}</span>
                                </button>
                              ))}
                            </div>
                          )}
                          
                          {/* Suggested items (when not searching) */}
                          {!showMenuItemSearch && menuItemSuggestions.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                              {menuItemSuggestions.map((item) => (
                                <Badge
                                  key={item.id}
                                  variant={currentMedia?.menuItemId === item.id ? 'default' : 'outline'}
                                  className={cn(
                                    "cursor-pointer",
                                    currentMedia?.menuItemId === item.id && "bg-primary text-primary-foreground"
                                  )}
                                  onClick={() => {
                                    const newMenuItemId = currentMedia?.menuItemId === item.id ? undefined : item.id;
                                    const newItemName = currentMedia?.menuItemId === item.id ? undefined : item.name;
                                    setMediaItems(prev => prev.map((m, idx) => 
                                      idx === currentMediaIndex 
                                        ? { ...m, menuItemId: newMenuItemId, itemName: newItemName }
                                        : m
                                    ));
                                  }}
                                >
                                  {currentMedia?.menuItemId === item.id && <Check className="w-3 h-3 mr-1" />}
                                  {item.name}
                                </Badge>
                              ))}
                            </div>
                          )}
                          
                          {!showMenuItemSearch && menuItemSuggestions.length === 0 && (
                            <p className="text-xs text-muted-foreground">
                              Type to search the menu
                            </p>
                          )}
                        </div>
                      )}
                      
                      {/* Show item rating when a menu item is selected for current media */}
                      {currentMedia?.itemName && (
                        <div className="bg-muted/30 rounded-lg p-3 space-y-3">
                          <div className="flex items-center gap-2">
                            <Utensils className="w-4 h-4 text-primary" />
                            <span className="font-medium">{currentMedia.itemName}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-muted-foreground">How was it?</span>
                            <div className="flex gap-1">
                              <Button
                                type="button"
                                size="sm"
                                variant={currentMedia?.itemRating === 3 ? 'default' : 'outline'}
                                className="text-xs px-3"
                                onClick={() => {
                                  triggerHaptic([10, 10, 10]);
                                  setMediaItems(prev => prev.map((m, idx) => 
                                    idx === currentMediaIndex ? { ...m, itemRating: 3 } : m
                                  ));
                                }}
                              >
                                <Heart className="w-3 h-3 mr-1" />
                                Loved it
                              </Button>
                              <Button
                                type="button"
                                size="sm"
                                variant={currentMedia?.itemRating === 2 ? 'default' : 'outline'}
                                className="text-xs px-3"
                                onClick={() => {
                                  triggerHaptic([10, 10]);
                                  setMediaItems(prev => prev.map((m, idx) => 
                                    idx === currentMediaIndex ? { ...m, itemRating: 2 } : m
                                  ));
                                }}
                              >
                                <Meh className="w-3 h-3 mr-1" />
                                OK
                              </Button>
                              <Button
                                type="button"
                                size="sm"
                                variant={currentMedia?.itemRating === 1 ? 'default' : 'outline'}
                                className="text-xs px-3"
                                onClick={() => {
                                  triggerHaptic(10);
                                  setMediaItems(prev => prev.map((m, idx) => 
                                    idx === currentMediaIndex ? { ...m, itemRating: 1 } : m
                                  ));
                                }}
                              >
                                <ThumbsDown className="w-3 h-3 mr-1" />
                                Hated it
                              </Button>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Non-Tier 2: Dish name + rating */}
              {!isTier2Restaurant && (
                currentMedia?.itemName && !isEditingItemName ? (
                  /* Compact confirmed state — dish name is set, section collapses to a single row */
                  <div className="flex items-center gap-2 bg-muted/30 rounded-lg px-3 py-2">
                    <Utensils className="w-4 h-4 text-primary flex-shrink-0" />
                    <span className="font-medium text-sm truncate flex-1 min-w-0">{currentMedia.itemName}</span>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <Button
                        type="button"
                        size="sm"
                        variant={currentMedia?.itemRating === 3 ? 'default' : 'outline'}
                        className="text-xs px-2"
                        onClick={() => {
                          triggerHaptic([10, 10, 10]);
                          setMediaItems(prev => prev.map((m, idx) =>
                            idx === currentMediaIndex ? { ...m, itemRating: 3 } : m
                          ));
                        }}
                      >
                        <Heart className="w-3 h-3" />
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant={currentMedia?.itemRating === 2 ? 'default' : 'outline'}
                        className="text-xs px-2"
                        onClick={() => {
                          triggerHaptic([10, 10]);
                          setMediaItems(prev => prev.map((m, idx) =>
                            idx === currentMediaIndex ? { ...m, itemRating: 2 } : m
                          ));
                        }}
                      >
                        <Meh className="w-3 h-3" />
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant={currentMedia?.itemRating === 1 ? 'default' : 'outline'}
                        className="text-xs px-2"
                        onClick={() => {
                          triggerHaptic(10);
                          setMediaItems(prev => prev.map((m, idx) =>
                            idx === currentMediaIndex ? { ...m, itemRating: 1 } : m
                          ));
                        }}
                      >
                        <ThumbsDown className="w-3 h-3" />
                      </Button>
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        data-testid="edit-dish-name"
                        onClick={() => {
                          setEditingItemNameValue(currentMedia.itemName || '');
                          setIsEditingItemName(true);
                        }}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  /* Full entry state — no confirmed name yet, or user is editing */
                  <div className="space-y-3">
                    <label className="text-sm font-medium flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-primary" />
                      Add a dish {mediaItems.length > 1 && `(Photo ${currentMediaIndex + 1})`}
                    </label>
                    <div className="bg-muted/30 rounded-lg p-3 space-y-3">
                      {isEditingItemName ? (
                        <div className="space-y-2">
                          <input
                            type="text"
                            placeholder="Enter dish name..."
                            value={editingItemNameValue}
                            onChange={(e) => setEditingItemNameValue(e.target.value)}
                            autoFocus
                            className="w-full px-3 py-2 text-sm border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary/20"
                            maxLength={200}
                          />
                          <div className="flex gap-2">
                            <Button
                              type="button"
                              size="sm"
                              variant="default"
                              onClick={() => {
                                setMediaItems(prev => prev.map((m, idx) =>
                                  idx === currentMediaIndex ? { ...m, itemName: editingItemNameValue.trim() } : m
                                ));
                                setIsEditingItemName(false);
                              }}
                              disabled={!editingItemNameValue.trim()}
                            >
                              <Check className="w-3 h-3 mr-1" />
                              Confirm
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setEditingItemNameValue("");
                                setIsEditingItemName(false);
                              }}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <Utensils className="w-4 h-4 text-primary flex-shrink-0" />
                            {currentMedia?.classification?.detectedItems?.[0] ? (
                              <span className="font-medium truncate">
                                {currentMedia.classification.detectedItems[0]}
                              </span>
                            ) : (
                              <span className="text-muted-foreground text-sm italic truncate">
                                Tap to name this dish
                              </span>
                            )}
                          </div>
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            data-testid="edit-dish-name"
                            onClick={() => {
                              setEditingItemNameValue(currentMedia?.classification?.detectedItems?.[0] || '');
                              setIsEditingItemName(true);
                            }}
                            className="flex-shrink-0"
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                        </div>
                      )}

                      {/* Item rating - only show when not editing name */}
                      {!isEditingItemName && (
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-muted-foreground">How was it?</span>
                          <div className="flex gap-1">
                            <Button
                              type="button"
                              size="sm"
                              variant={currentMedia?.itemRating === 3 ? 'default' : 'outline'}
                              className="text-xs px-3"
                              onClick={() => {
                                triggerHaptic([10, 10, 10]);
                                setMediaItems(prev => prev.map((m, idx) =>
                                  idx === currentMediaIndex ? { ...m, itemRating: 3 } : m
                                ));
                              }}
                            >
                              <Heart className="w-3 h-3 mr-1" />
                              Loved it
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant={currentMedia?.itemRating === 2 ? 'default' : 'outline'}
                              className="text-xs px-3"
                              onClick={() => {
                                triggerHaptic([10, 10]);
                                setMediaItems(prev => prev.map((m, idx) =>
                                  idx === currentMediaIndex ? { ...m, itemRating: 2 } : m
                                ));
                              }}
                            >
                              <Meh className="w-3 h-3 mr-1" />
                              OK
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant={currentMedia?.itemRating === 1 ? 'default' : 'outline'}
                              className="text-xs px-3"
                              onClick={() => {
                                triggerHaptic(10);
                                setMediaItems(prev => prev.map((m, idx) =>
                                  idx === currentMediaIndex ? { ...m, itemRating: 1 } : m
                                ));
                              }}
                            >
                              <ThumbsDown className="w-3 h-3 mr-1" />
                              Hated it
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )
              )}

              {/* Add more media button - orange camera icon */}
              {mediaItems.some(m => m.classification?.contentType === 'food-item') && (
                <button
                  type="button"
                  onClick={() => setStep('camera')}
                  className="flex items-center gap-2 py-2 text-primary"
                  data-testid="add-more-media-btn"
                >
                  <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center relative">
                    <Camera className="w-5 h-5 text-primary-foreground" />
                    <Plus className="w-3 h-3 text-primary-foreground absolute bottom-1.5 right-1.5" />
                  </div>
                  <span className="text-sm font-medium">Add another dish</span>
                </button>
              )}

              <QuickTagSelector
                selectedTags={selectedTags}
                onTagsChange={setSelectedTags}
                contentType={mediaItems[0]?.classification?.contentType}
                cuisineHint={mediaItems[0]?.classification?.cuisineHint}
                aiSuggestedTags={mediaItems.flatMap(m => m.classification?.suggestedTags || [])}
                serviceType={serviceType}
                rating={rating[0]}
                restaurantCuisine={selectedRestaurant?.brandCategoryName}
              />

              <div className="space-y-2">
                <label className="text-sm font-medium">Your recommendations</label>
                <Textarea
                  ref={textareaRef}
                  value={reviewText}
                  onChange={(e) => setReviewText(e.target.value)}
                  placeholder="What should others know? Any must-orders or tips for first-timers?"
                  className="resize-none"
                  rows={4}
                />
              </div>

              
              {/* Restaurant/Experience Rating - Summary at bottom */}
              <div className="space-y-3 pt-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium flex items-center gap-2">
                    <Star className="w-4 h-4" />
                    Overall Experience
                  </label>
                  <span className={cn("font-bold text-lg", getRatingColor(rating[0]))}>
                    {rating[0].toFixed(1)} - {getRatingLabel(rating[0])}
                  </span>
                </div>
                <div className="relative pt-2 pb-4">
                  <Slider
                    value={rating}
                    onValueChange={setRating}
                    min={1}
                    max={10}
                    step={0.1}
                    className="w-full"
                  />
                  
                  {/* Prior review notches */}
                  <div className="absolute top-0 left-0 right-0 h-2 pointer-events-none">
                    {priorReviews.map((review, idx) => (
                      <div
                        key={review.reviewId || idx}
                        className="absolute top-0 w-0.5 h-2 bg-muted-foreground/40"
                        style={{ left: `${((review.score - 1) / 9) * 100}%` }}
                        title={`${review.restaurant}: ${review.score}`}
                      />
                    ))}
                  </div>
                </div>
                
                {/* Show comparisons to prior reviews */}
                {priorReviews.length > 0 && (
                  <div className="text-xs text-muted-foreground space-y-1">
                    {priorReviews.map((review) => {
                      const diff = rating[0] - review.score;
                      if (Math.abs(diff) < 0.5) {
                        return (
                          <div key={review.reviewId} className="flex items-center gap-2">
                            <div className="w-1 h-1 rounded-full bg-muted-foreground/40" />
                            <span>Similar to {review.restaurant} ({review.score.toFixed(1)})</span>
                          </div>
                        );
                      }
                      return null;
                    })}
                  </div>
                )}
              </div>

              {/* Tag Friends Section */}
              <div className="pt-2 space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Tag Friends
                  </label>
                  <TagFriendsPopover
                    following={followingData?.users ?? []}
                    selected={taggedFriends}
                    onAdd={(id) => setTaggedFriends(prev => [...prev, id])}
                    onRemove={(id) => setTaggedFriends(prev => prev.filter(x => x !== id))}
                  />
                </div>
                <SelectedFriendChips
                  following={followingData?.users ?? []}
                  selected={taggedFriends}
                  onRemove={(id) => setTaggedFriends(prev => prev.filter(x => x !== id))}
                />
              </div>

              {!selectedRestaurant && (
                <Card className="border-destructive bg-destructive/5">
                  <CardContent className="p-3 flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-destructive mt-0.5" />
                    <p className="text-sm text-destructive">
                      Please select a restaurant to post your review
                    </p>
                  </CardContent>
                </Card>
              )}
              
            </>
          )}
        </div>
        
        {/* Bottom Sticky Bar with Post Button and Kudoz Indicator */}
        {step === 'review' && (
          <div className="flex-shrink-0 bg-background border-t px-4 py-3">
            <div className="flex items-center justify-between gap-3">
              {/* Kudoz Potential Indicator */}
              <div className="flex items-center gap-2">
                <KudozModule
                  earned={kudozBreakdown.earned}
                  total={kudozBreakdown.total}
                  showProgress={true}
                  size="sm"
                />
                <div className="text-xs text-muted-foreground">
                  <span className="font-medium text-kudoz">{kudozBreakdown.earned}</span>
                  <span> earned</span>
                </div>
              </div>
              
              {/* Post Button - soft gate when no review text */}
              <Button 
                onClick={() => {
                  if (reviewText.trim() === '') {
                    setShowPostWithoutTextDialog(true);
                  } else {
                    handleSubmit();
                  }
                }}
                disabled={isSubmitting || !selectedRestaurant}
                className="min-w-[100px]"
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-1.5">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {submitPhase === 'uploading' ? 'Uploading…' : 'Posting…'}
                  </span>
                ) : (
                  "Post"
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Soft Post Gate — shown when Post tapped with empty review text */}
        <Dialog open={showPostWithoutTextDialog} onOpenChange={setShowPostWithoutTextDialog}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Post without a review?</DialogTitle>
              <DialogDescription>
                A short note about your experience helps others discover great food. Your photos and rating will still be shared.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex-col gap-2 sm:flex-col">
              <Button
                variant="default"
                className="w-full"
                onClick={() => {
                  setShowPostWithoutTextDialog(false);
                  setTimeout(() => {
                    textareaRef.current?.focus();
                    textareaRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }, 150);
                }}
              >
                Write something
              </Button>
              <Button
                variant="ghost"
                className="w-full"
                onClick={() => {
                  setShowPostWithoutTextDialog(false);
                  handleSubmit();
                }}
              >
                Post anyway
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
      
      <GeolocationPrompt
        isOpen={showLocationPrompt}
        onClose={() => setShowLocationPrompt(false)}
        onAllow={handleLocationAllow}
        onDeny={handleLocationDeny}
        mode={locationPromptMode}
      />
      
    </Dialog>
  );
}
